import { PrimitiveAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

type KeyOfMap<M> = M extends Map<infer K, any> ? K : never;
type ValueOfMap<M> = M extends Map<any, infer V> ? V : never;

export function useMapAtomValue<M extends Map<any, any>>(
  mapAtom: PrimitiveAtom<M>,
  key: KeyOfMap<M>,
  defaultValue: ValueOfMap<M>,
): ValueOfMap<M> {
  const mapValue = useAtomValue(mapAtom);

  return useMemo(() => {
    return mapValue.get(key) ?? defaultValue;
  }, [mapValue, key, defaultValue]);
}

export function useMapAtomSetter<M extends Map<any, any>>(
  mapAtom: PrimitiveAtom<M>,
  key: KeyOfMap<M>,
): (newValue: ValueOfMap<M>) => void {
  const setMapValue = useSetAtom(mapAtom);

  return useCallback(
    (newValue: ValueOfMap<M>) => {
      setMapValue(prev => {
        const next = new Map(prev);
        next.set(key, newValue);
        return next as M;
      });
    },
    [setMapValue, key],
  );
}

export function useMapAtom<M extends Map<any, any>>(
  mapAtom: PrimitiveAtom<M>,
  key: KeyOfMap<M>,
  defaultValue: ValueOfMap<M>,
): [ValueOfMap<M>, (val: ValueOfMap<M>) => void] {
  const value = useMapAtomValue(mapAtom, key, defaultValue);
  const setter = useMapAtomSetter(mapAtom, key);

  return useMemo(() => [value, setter] as const, [value, setter]);
}
